import AutoStories from '@mui/icons-material/AutoStories';
import Event from '@mui/icons-material/Event';
import Groups from '@mui/icons-material/Groups';
import LocationOn from '@mui/icons-material/LocationOn';
import Person from '@mui/icons-material/Person';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { AnyEntity } from '@/lib/types';
import { resolvePrimaryImage } from '@/lib/utils/image-identity';

import { SecureImage } from '@/components/secure-image';

export interface RelationshipEntityHeroProps {
  entity: AnyEntity;
  entityKindLabel: string;
  onAddRelationship: () => void;
}

function getPlaceholderIcon(entityType: string) {
  const iconProps = { sx: { color: 'primary.main', fontSize: { xs: 64, md: 76 }, opacity: 0.55 } };

  switch (entityType) {
    case 'locations':
      return <LocationOn {...iconProps} />;
    case 'organizations':
      return <Groups {...iconProps} />;
    case 'events':
      return <Event {...iconProps} />;
    case 'codex':
      return <AutoStories {...iconProps} />;
    default:
      return <Person {...iconProps} />;
  }
}

export function RelationshipEntityHero({
  entity,
  entityKindLabel,
  onAddRelationship,
}: Readonly<RelationshipEntityHeroProps>) {
  const { t } = useTranslation();
  const images = 'images' in entity && Array.isArray(entity.images) ? entity.images : undefined;
  const primaryImageId = 'primaryImageId' in entity && typeof entity.primaryImageId === 'string' ? entity.primaryImageId : undefined;
  const mainImageUrl = 'mainImageUrl' in entity && typeof entity.mainImageUrl === 'string' ? entity.mainImageUrl : undefined;
  const mainImagePath = 'mainImagePath' in entity && typeof entity.mainImagePath === 'string' ? entity.mainImagePath : undefined;

  const primaryImage = resolvePrimaryImage({ images, primaryImageId, mainImageUrl, mainImagePath });
  const imageUrl = primaryImage?.profile?.url
    || primaryImage?.original?.url
    || mainImageUrl
    || ('coverImageUrl' in entity && typeof entity.coverImageUrl === 'string' ? entity.coverImageUrl : undefined);
  const imagePath = primaryImage?.profile?.path
    || primaryImage?.original?.path
    || mainImagePath;
  const imageSource = imageUrl || imagePath;

  return (
    <Card
      component="section"
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          alignItems: { xs: 'center', md: 'stretch' },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2.5, md: 4 },
          p: { xs: 2.5, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: 'action.hover',
            border: 4,
            borderColor: 'background.paper',
            borderRadius: '50%',
            boxShadow: 3,
            display: 'flex',
            flexShrink: 0,
            height: { xs: 144, md: 184 },
            justifyContent: 'center',
            overflow: 'hidden',
            position: 'relative',
            width: { xs: 144, md: 184 },
          }}
        >
          {imageSource ? (
            <SecureImage
              src={imageSource}
              path={imagePath}
              alt={entity.name}
              sx={{
                height: '100%',
                inset: 0,
                objectFit: 'cover',
                objectPosition: entity.entityType === 'characters' ? 'center top' : 'center',
                position: 'absolute',
                width: '100%',
              }}
            />
          ) : (
            getPlaceholderIcon(entity.entityType)
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            width: '100%',
          }}
        >
          <Chip
            label={entityKindLabel}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ alignSelf: 'flex-start', mb: 1.5, fontWeight: 700 }}
          />
          <Typography component="h2" variant="h2" sx={{ overflowWrap: 'anywhere' }}>
            {entity.name}
          </Typography>

          {entity.shortDescription && (
            <Typography
              component="p"
              variant="h5"
              sx={{
                color: 'text.secondary',
                display: '-webkit-box',
                mb: 0,
                mt: 1.25,
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
              }}
            >
              {entity.shortDescription}
            </Typography>
          )}

          <Box sx={{ mt: 'auto', pt: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
              <Button
                component={Link}
                to={`/${entity.entityType}/view/${entity.id}`}
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {t('relationships.hero.view_full_profile')}
              </Button>
              <Button
                variant="contained"
                onClick={onAddRelationship}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {t('relationships.add_title')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}
